import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { BigButton, Card } from "../design/BigButton";

export function PurchasesScreen() {
  const [products, setProducts] = useState([]);
  const [status, setStatus] = useState(null);
  const [msg, setMsg] = useState(null);
  const [paymentMode, setPaymentMode] = useState(null);   // null=loading, "live"|"sandbox"|"error"
  const [modeError, setModeError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [redirecting, setRedirecting] = useState(false);

  const fetchMode = () => {
    setModeError(null);
    setPaymentMode(null);
    api.get("/purchases/mode")
      .then((r) => setPaymentMode(r.data.mode))
      .catch((e) => {
        setPaymentMode("error");
        setModeError(String(e.response?.data?.detail || e.message));
      });
  };

  useEffect(() => {
    fetchMode();
    api.get("/purchases/products").then((r) => setProducts(r.data.items || []));
    api.post("/purchases/restore").then((r) => setStatus(r.data.subscriptionStatus)).catch(() => {});
  }, []);

  async function buy(_id) {
    // Live Stripe Checkout is the only supported buy path from the UI.
    if (paymentMode !== "live") return;
    setBusy(true); setRedirecting(true); setMsg(null);
    try {
      const r = await api.post("/purchases/checkout", {});
      if (r.data.url) {
        window.location.href = r.data.url;
        return;
      }
      setMsg("Could not start Stripe checkout — no URL returned.");
      setRedirecting(false);
    } catch (e) {
      setMsg(String(e.response?.data?.detail || e.message) || "Couldn't reach checkout. Try again.");
      setRedirecting(false);
    } finally {
      setBusy(false);
    }
  }

  const canBuy = paymentMode === "live" && !busy && !redirecting && status !== "premium";
  let buyLabel;
  if (redirecting) buyLabel = "Redirecting…";
  else if (busy) buyLabel = "Processing…";
  else if (paymentMode === null) buyLabel = "Loading…";
  else if (paymentMode !== "live") buyLabel = "Unavailable";
  else buyLabel = "Buy";

  const modeBadge = paymentMode === "error" ? (
    <span
      data-testid="purchases-mode-error"
      style={{
        display: "inline-block", marginLeft: 10, padding: "2px 8px",
        background: "#8a0e18", color: "#fff", borderRadius: 999,
        fontSize: 12, fontWeight: 700, letterSpacing: 0.2, verticalAlign: "middle",
      }}
    >
      Purchase temporarily unavailable{" · "}
      <button
        type="button"
        onClick={fetchMode}
        data-testid="purchases-mode-retry"
        style={{ background: "transparent", border: "none", color: "#fff", textDecoration: "underline", cursor: "pointer", padding: 0, fontWeight: 700 }}
      >Retry</button>
    </span>
  ) : null;

  return (
    <div data-testid="purchases-screen">
      <h1>Family Unlock{modeBadge}</h1>
      <p>Current status: <b data-testid="purchases-status">{status || "loading…"}</b></p>
      {status === "premium" ? (
        <Card data-testid="purchases-owned">
          <h3>You&apos;re unlocked ✓</h3>
          <p>Every world, every level, every child profile — enjoy!</p>
        </Card>
      ) : products.length === 0 ? (
        <Card data-testid="purchases-sandbox-banner">Purchases aren&apos;t configured yet. Please contact the administrator.</Card>
      ) : (
        products.map((p) => (
          <Card key={p.productId} data-testid={`purchase-${p.productId}`}>
            <h3>{p.name}</h3><p>{p.description}</p><p>${p.priceUsd}</p>
            {status === "premium" ? (
              <BigButton disabled data-testid="purchases-bought">Owned ✓</BigButton>
            ) : (
              <BigButton
                onClick={() => buy(p.productId)}
                disabled={!canBuy}
                aria-busy={busy || redirecting}
                data-testid={`buy-${p.productId}`}
              >{buyLabel}</BigButton>
            )}
          </Card>
        ))
      )}
      <BigButton variant="ghost" onClick={async () => {
        const r = await api.post("/purchases/restore");
        setStatus(r.data.subscriptionStatus); setMsg("Restored");
      }} data-testid="purchases-restore">Restore purchases</BigButton>
      {msg && <p data-testid="purchases-msg">{msg}</p>}
    </div>
  );
}

export function PrivacyScreen() {
  const { logout } = useAuth();
  const [confirmText, setConfirmText] = useState("");
  const [msg, setMsg] = useState(null);

  async function exportData() {
    const { data } = await api.get("/privacy/export");
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "wmleye-export.json";
    a.click();
    setMsg("Downloaded");
  }
  async function del() {
    try {
      await api.post("/privacy/delete-account", { confirmPhrase: confirmText });
      await logout();
      setMsg("Account marked for deletion.");
    } catch (e) { setMsg(String(e.response?.data?.detail || e.message)); }
  }

  return (
    <div data-testid="privacy-screen">
      <h1>Privacy</h1>
      <Card>
        <h3>What we collect</h3>
        <ul>
          <li>Parent email + password hash</li>
          <li>Child nickname, avatar choice, age band, difficulty preference</li>
          <li>Gameplay events (which objects found, hints used, session timing)</li>
          <li>Token transactions (append-only ledger)</li>
        </ul>
        <p>No third-party trackers. No ads. No analytics by default.</p>
      </Card>
      <BigButton onClick={exportData} data-testid="privacy-export">Export my data</BigButton>
      <h2>Delete account</h2>
      <p>Type <b>delete my account</b> to confirm.</p>
      <input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} data-testid="privacy-confirm" />
      <BigButton variant="coral" onClick={del} disabled={confirmText !== "delete my account"} data-testid="privacy-delete">Delete</BigButton>
      {msg && <p data-testid="privacy-msg">{msg}</p>}
    </div>
  );
}

export function SupportScreen() {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sent, setSent] = useState(false);
  const { user } = useAuth();
  async function submit() {
    await api.post("/support", { subject, message, email: user?.email });
    setSent(true);
  }
  return (
    <div data-testid="support-screen">
      <h1>Support</h1>
      <Card>
        <h3>FAQ</h3>
        <p><b>Tokens?</b> Kids earn them for finding objects. Server-validated, tamper-proof.</p>
        <p><b>Hints?</b> Four levels — cheapest is a spoken clue.</p>
        <p><b>Difficulty?</b> Change any time in the profile settings.</p>
        <p><b>Delete account?</b> Privacy tab. 30-day grace period.</p>
        <p><b>PIN reset?</b> Sign out then use "Forgot password" on the login screen.</p>
      </Card>
      <h2>Contact us</h2>
      {sent ? (
        <p data-testid="support-sent">Thanks! We got it.</p>
      ) : (
        <>
          <input placeholder="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="support-subject" />
          <textarea placeholder="Tell us what's up" value={message} onChange={(e) => setMessage(e.target.value)} data-testid="support-message" />
          <BigButton onClick={submit} disabled={!subject || !message} data-testid="support-submit">Send</BigButton>
        </>
      )}
    </div>
  );
}
